import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const Item = ({ item, index, moveItem }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'item',
    item: { index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'item',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      style={{ opacity: isDragging ? 0.5 : 1, padding: '8px', border: '1px solid var(--border-color)', margin: '4px', cursor: 'move' }}
    >
      {item.name}
    </div>
  );
};

const SystemTopics = () => {
  const [topics, setTopics] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState([]);
  const [itemInput, setItemInput] = useState('');
  const [message, setMessage] = useState('');
  const [editTopicId, setEditTopicId] = useState(null);


// Construct API URL
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';

  const fetchTopics = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/topics/system`);
      //console.log('Fetched system topics:', res.data);
      setTopics(res.data);
    } catch (err) {
      //console.error('Error fetching system topics:', err);
      setMessage('Failed to fetch system topics');
    }
  };

  useEffect(() => {
    fetchTopics();
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${apiUrl}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(response => {
          setIsAdmin(response.data.user.role === 'admin');
        })
        .catch(error => console.error('Failed to verify user role:', error));
    }
  }, []);

  const handleCreateOrUpdateTopic = async () => {
    const token = localStorage.getItem('token');
    try {
      const payload = { name, description, items: items.map((item, index) => ({ name: item.name, position: index + 1 })) };
      const url = editTopicId ? `${apiUrl}/api/topics/system/${editTopicId}` : `${apiUrl}/api/topics/system`;
      const method = editTopicId ? 'put' : 'post';
      const response = await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTopics();
      setName('');
      setDescription('');
      setItems([]);
      setItemInput('');
      setEditTopicId(null);
      setMessage(editTopicId ? 'System topic updated successfully' : 'System topic created successfully');
    } catch (error) {
      //console.error('Error saving system topic:', error);
      setMessage('Failed to save system topic: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleEditTopic = (topic) => {
    setName(topic.name);
    setDescription(topic.description || '');
    setItems(topic.items.map(item => ({ name: item.name })));
    setItemInput('');
    setEditTopicId(topic._id);
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Are you sure you want to delete this system topic?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${apiUrl}/api/topics/system/${topicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTopics();
      setMessage('System topic deleted successfully');
    } catch (error) {
      //console.error('Error deleting system topic:', error);
      setMessage('Failed to delete system topic: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleAddItem = () => {
    if (itemInput.trim()) {
      const newItems = itemInput.split(',').map(item => item.trim()).filter(item => item).map(name => ({ name }));
      setItems([...items, ...newItems]);
      setItemInput('');
    }
  };

  const moveItem = (fromIndex, toIndex) => {
    const updatedItems = [...items];
    const [movedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, movedItem);
    setItems(updatedItems);
  };


  return (
    <DndProvider backend={HTML5Backend}>
      <div>
        <h1>Official Rankings</h1>
        {message && <p>{message}</p>}
        {isAdmin && (
          <div>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Topic Name" />
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description" />
            <input
              value={itemInput}
              onChange={e => setItemInput(e.target.value)}
              placeholder="Add item (comma-separated)"
            />
            <button onClick={handleAddItem}>Add Item</button>
            <div>
              <h3>Items (Drag to reorder)</h3>
              {items.map((item, index) => (
                <Item key={item.name + index} item={item} index={index} moveItem={moveItem} />
              ))}
            </div>
            <button onClick={handleCreateOrUpdateTopic}>{editTopicId ? 'Update System Topic' : 'Create System Topic'}</button>
          </div>
        )}
        {isAdmin && (
          <>
            <h2>System Topics</h2>
            {topics.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid var(--border-color)', padding: '8px' }}>Name</th>
                    <th style={{ border: '1px solid var(--border-color)', padding: '8px' }}>Description</th>
                    <th style={{ border: '1px solid var(--border-color)', padding: '8px' }}>Items</th>
                    {isAdmin && <th style={{ border: '1px solid var(--border-color)', padding: '8px' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {topics.map(topic => (
                    <tr key={topic._id}>
                      <td style={{ border: '1px solid var(--border-color)', padding: '8px' }}>{topic.name}</td>
                      <td style={{ border: '1px solid var(--border-color)', padding: '8px' }}>{topic.description || '-'}</td>
                      <td style={{ border: '1px solid var(--border-color)', padding: '8px' }}>
                        {topic.items.map(item => item.name).join(', ')}
                      </td>
                      {isAdmin && (
                        <td style={{ border: '1px solid var(--border-color)', padding: '8px' }}>
                          <button onClick={() => handleEditTopic(topic)}>Edit</button>
                          <button onClick={() => handleDeleteTopic(topic._id)}>Delete</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No system topics available.</p>
            )}
          </>
        )}

        <h2>Ranking Cards</h2>
        <div className="topic-cards">
          {topics.map(topic => (
            <div key={topic._id} className="topic-card">
              <h3>{topic.name}</h3>
              <h4>{topic.description || 'No description'}</h4>
              <div>
                <ol>
                  {topic.items.slice(0, 25).map((item, index) => (
                    <li key={index}>{item.name}</li>
                  ))}
                </ol>
              </div>
              <Link to={`/topic/${topic._id}`}>View Details</Link>
            </div>
          ))}
        </div>
      </div>
    </DndProvider>
  );
};

export default SystemTopics;